import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    FormLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import PublicOffIcon from "@mui/icons-material/PublicOff";
import PublicIcon from '@mui/icons-material/Public';
import BaseDialog from '../../../BaseDialog';
import { Story } from '../../../../services/restserver-openapi';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { SingleImageUploader } from "../../../presentationalComponents/SingleImageUploader";
import { asyncUploadImageForTour, removeUpload } from "../../../../store/Vanlifer/tourSlice";

type SharingMode = Story.sharingMode;

type EditTourDetailsDialogProps = {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        name: string;
        description: string;
        sharingMode: SharingMode;
        hasNewImage: boolean;
        imageName?: string;
        fileExtension?: string;
    }) => Promise<void>;
    tourId?: number;
    initialTitle?: string;
    initialDescription?: string;
    initialSharingMode?: SharingMode;
    initialSasLink?: string;
};

export default function EditTourDetailsDialog({
                                                  open,
                                                  onClose,
                                                  onSave,
                                                  tourId,
                                                  initialTitle = '',
                                                  initialDescription = '',
                                                  initialSharingMode = Story.sharingMode.PRIVATE,
                                                  initialSasLink,
                                              }: EditTourDetailsDialogProps) {
    type TourImage = {
        id: string;
        url: string | null;
        fileExtension: string | undefined;
    };

    const dispatch = useAppDispatch();

    const upload = useAppSelector((state) => state.tour.upload);

    // Determine if an image is still uploading or has failed; if so, disable the saving
    const isSaveDisabled = useMemo(() => {
        return upload ? upload.status !== 'success' : false;
    }, [upload]);

    const [confirmedImage, setConfirmedImage] = useState<TourImage | undefined>();
    const onUpload = (file: File, localId: string) => {
        if (!tourId) return;
        dispatch(asyncUploadImageForTour({ tourId, file, localId }));
    };

    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState(initialDescription);
    const [sharingMode, setSharingMode] = useState<SharingMode>(initialSharingMode);

    const [saving, setSaving] = useState(false);

    const [showNameRequiredHint, setShowNameRequiredHint] = useState(false);

    const userEntitlements = useAppSelector((state) => state.user.entitlements);

    const canPublishContent = useMemo(() => {
        return userEntitlements.some(
            (entitlement) =>
                entitlement.featureId === 'vanliferpublishcontent' &&
                entitlement.value === 'true'
        );
    }, [userEntitlements]);

    // Reset form state iff dialog is opened
    useEffect(() => {
        if (open) {
            setTitle(initialTitle);
            setDescription(initialDescription);
            setSharingMode(initialSharingMode);
            setShowNameRequiredHint(false);

            if (initialSasLink) {
                setConfirmedImage({ id: 'initial-image', url: initialSasLink, fileExtension: undefined });
            } else {
                setConfirmedImage(undefined);
            }

            dispatch(removeUpload());
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async () => {
        const trimmedTitle = title.trim();
        const trimmedDescription = description.trim();

        const isTitleEmpty = trimmedTitle.length === 0;
        const isTitleTooLong = trimmedTitle.length > 50;
        const isDescriptionTooLong = trimmedDescription.length > 200;

        setShowNameRequiredHint(isTitleEmpty);

        if (isTitleEmpty || isTitleTooLong || isDescriptionTooLong) {
            return;
        }

        setSaving(true);
        try {
            await Promise.resolve(onSave({
                name: title.trim(),
                description,
                sharingMode,
                hasNewImage: confirmedImage?.id !== 'initial-image' || false,
                imageName: confirmedImage?.id === 'initial-image' ? undefined : confirmedImage?.id,
                fileExtension: confirmedImage?.id === 'initial-image' ? undefined : confirmedImage?.fileExtension,
            }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            onSave={handleSave}
            disableSave={isSaveDisabled || saving}
            title="Reisedetails bearbeiten"
            saving={saving}
        >
            <Box width="100%">
                <FormControl required fullWidth>
                    <TextField
                        fullWidth
                        label="Titel"
                        value={title}
                        onChange={(e) => {
                            const value = e.target.value;
                            setTitle(value);
                            setShowNameRequiredHint(value.trim() === '');
                        }}
                        margin="dense"
                        required
                        inputProps={{ maxLength: 70 }}
                        error={showNameRequiredHint || title.trim().length > 50}
                    />
                    <FormHelperText
                        error={showNameRequiredHint || title.trim().length > 50}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 0.5,
                        }}
                    >
                        <span style={{ visibility: showNameRequiredHint ? 'visible' : 'hidden' }}>
                        Der Titel darf nicht leer sein.
                        </span>
                        <span >
                            {title.trim().length}/50 Zeichen
                        </span>
                    </FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                    <TextField
                        fullWidth
                        label="Beschreibung"
                        multiline
                        minRows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        margin="dense"
                        inputProps={{ maxLength: 220 }}
                        error={description.trim().length > 200}
                        helperText={
                            <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <span style={{ color: description.trim().length > 200 ? 'red' : undefined }}>
                                    {description.trim().length}/200 Zeichen
                                </span>
                            </span>
                        }
                    />
                </FormControl>
            </Box>

            <SingleImageUploader
                confirmedImage={confirmedImage}
                setConfirmedImage={setConfirmedImage}
                upload={upload ?? undefined}
                onUpload={onUpload}
                onRemoveUpload={() => dispatch(removeUpload())}
            />

            <Box mt={2}>
                <FormControl fullWidth margin="normal">
                    <FormLabel>Sichtbarkeit</FormLabel>
                    <ToggleButtonGroup
                        value={sharingMode}
                        exclusive
                        onChange={(_, value) => {
                            if (value !== null) setSharingMode(value);
                        }}
                        fullWidth
                        color="primary"
                    >
                        <Tooltip title="Nur du und Personen mit dem Link können diese Reise sehen.">
                            <ToggleButton value={Story.sharingMode.PRIVATE}>
                                <PublicOffIcon sx={{ mr: 1 }} />
                                Privat
                            </ToggleButton>
                        </Tooltip>

                        <Tooltip title="Alle können diese Reise sehen, sie wird auch bei der Reisen-Entdecken-Seite angezeigt.">
                            <ToggleButton
                                value={Story.sharingMode.PUBLIC}
                                disabled={!canPublishContent}
                            >
                                <PublicIcon sx={{ mr: 1 }} />
                                Öffentlich
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </FormControl>
            </Box>
        </BaseDialog>
    );
}
